import { useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Currency,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  useLazyGetContractDocumentFileQuery,
  useLazyGetCustomerBeneficiaryContractQuery,
  useUpdateContractMutation,
  useUpdateContractStatusMutation,
} from '../../../customers/store/services/customers';
import {
  BeneficiaryContract,
  BeneficiaryContractFormValues,
  BeneficiaryStatus,
  ContractStatement,
} from '../types';
import formatContractStatementsList from '../helpers/format-contract-statements';
import { OrganizationType } from '../../types';
import { clearContractState } from '../../store/slices/beneficiaries';

import ROUTES from '../../../../routes';

const useBeneficiaryContracts = ({
  beneficiaryContract,
  activePermissions,
  beneficiaryId,
  contractId,
  type,
  customerId,
  currenciesList,
}: {
  beneficiaryContract?: BeneficiaryContract;
  activePermissions?: PermissionEnum[];
  beneficiaryId?: string;
  contractId?: string;
  type: OrganizationType;
  customerId?: string;
  currenciesList?: Currency[];
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [panelState, setPanelState] = useState<'details' | 'transactions'>(
    'details',
  );
  const [uploadedDocuments, setUploadedDocuments] = useState<
    { uploadId: string; createdAt: string; name?: string; id: string }[]
  >([]);
  const [updateError, setUpdateError] = useState('');
  const [downloadId, setDownloadId] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>();

  const [updateContract, { isLoading: updateInProgress }] =
    useUpdateContractMutation();
  const [updateContractStatus, { isLoading: updateStatusInProgress }] =
    useUpdateContractStatusMutation();
  const [
    getContractFile,
    { isLoading: downloadInProgress, isFetching: downloadIsFetching },
  ] = useLazyGetContractDocumentFileQuery();
  const [getContract, { isLoading, isFetching }] =
    useLazyGetCustomerBeneficiaryContractQuery();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    control,
    reset,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm<BeneficiaryContractFormValues>();

  const onRowClick = (id: string) => {
    const currentStatement = beneficiaryContract?.statementItems?.edges?.find(
      (item) => item.node.id === id,
    );

    if (currentStatement) {
      setPanelIsOpen(false);

      let link = '';

      if (
        currentStatement.node.type === 'INTERNAL' &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_INTERNAL_LIST)
      ) {
        link = `/transactions/internal/${currentStatement?.node.transactionId}`;
      } else if (
        currentStatement.node.type === 'OUTGOING' &&
        activePermissions?.includes(PermissionEnum.TRANSACTIONS_OUTGOING_LIST)
      ) {
        link = `/transactions/outgoing/${currentStatement?.node.transactionId}`;
      }

      window.open(link, '_blank');
    }
  };

  const getFile = async (id: string) => {
    setDownloadId(id);
    setDownloadError('');

    const response = (await getContractFile({
      id: beneficiaryId as string,
      contractId: contractId as string,
      documentId: id,
    })) as {
      error?: ResponseError;
      data?: any;
    };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setDownloadError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setDownloadError(response.error.data.message);
        } else {
          setDownloadError('Internal Server Error. Try Again');
        }
      }
    } else if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      const currentDocument = uploadedDocuments?.find((item) => item.id === id);

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = currentDocument?.name || 'Document';
      hiddenElement.click();
      setDownloadId('');
    }
  };

  const updateContractAction = handleSubmit(async (values) => {
    setUpdateError('');

    const body: {
      id: string;
      name: string;
      description?: string;
      sum?: number;
      currencyId?: string;
    } = {
      id: values.id,
      name: values.name,
      description: values.description,
      currencyId: values?.currencyId || '',
    };

    if (values?.sum) {
      body.sum = Number(values?.sum);
    }

    if (values?.currencyId) {
      body.currencyId = values.currencyId;
    }

    if (values.description) {
      body.description = values?.description;
    }

    const response = (await updateContract({
      id: beneficiaryId as string,
      contractId: contractId as string,
      body,
    })) as { error?: ResponseError };

    if (
      values.status !== beneficiaryContract?.status &&
      beneficiaryContract?.status !== 'REJECTED'
    ) {
      await updateContractStatus({
        id: beneficiaryId as string,
        contractId: contractId as string,
        body: {
          status: values.status as BeneficiaryStatus,
        },
      });
    }

    if (response.error && response.error.data.message) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError: setUpdateError,
      });
    }
  });

  const handleClose = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      dispatch(clearContractState());

      navigate(
        generatePath(
          type === OrganizationType.INDIVIDUAL
            ? ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARY
            : ROUTES.CUSTOMERS_CORPORATE_BENEFICIARY,
          {
            customerId: customerId as string,
            beneficiaryId: beneficiaryId as string,
          },
        ),
      );
    }, 100);
  };

  const formattedData = useMemo(() => {
    const data = formatContractStatementsList(
      beneficiaryContract?.statementItems?.edges?.map(
        (item) => item.node,
      ) as ContractStatement[],
    );

    return data;
  }, [beneficiaryContract]);

  useEffect(() => {
    if (contractId) {
      getContract({ beneficiaryId: beneficiaryId as string, contractId });
    }
  }, [contractId]);

  useEffect(() => {
    if (beneficiaryContract && currenciesList) {
      reset({
        id: beneficiaryContract.id,
        name: beneficiaryContract.name,
        description: beneficiaryContract.description,
        sum: beneficiaryContract?.sum,
        status: beneficiaryContract?.status,
        currencyId: beneficiaryContract?.currencyId,
      });

      if (beneficiaryContract?.currencyId) {
        setSelectedCurrency(
          currenciesList?.find(
            (item) => item.id === beneficiaryContract?.currencyId,
          ),
        );
      }

      setUploadedDocuments(
        beneficiaryContract?.supportingDocuments?.edges?.map(
          (item) => item.node,
        ) || [],
      );
    }
  }, [beneficiaryContract, currenciesList]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  return {
    isLoading,
    isFetching,
    panelState,
    setPanelState,
    onRowClick,
    formattedData,
    control,
    errors,
    getFile,
    downloadId,
    downloadError,
    downloadInProgress,
    selectedCurrency,
    setSelectedCurrency,
    uploadedDocuments,
    downloadIsFetching,
    panelIsOpen,
    handleClose,
    updateError,
    updateInProgress,
    updateStatusInProgress,
    updateContractAction,
  };
};

export default useBeneficiaryContracts;
